import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { Spinner } from 'admin-components/components';
import { FIND_VOUCHER_BY_REFERENCE, SEARCH_VOUCHERS } from 'store/queries';
import SearchVoucherByCodeForm from './SearchVoucherByCodeForm';
import SearchVoucherByEmailForm from './SearchVoucherByEmailForm';
import { StyledManageVoucherContainer, StyledSearchByHeader, StyledSearchSuggestionsList } from './ManageVoucher.styles';
import { VOUCHER_TYPE_PREPAID } from '../../constants/vouchers';
import { Column } from '../../components/shared/Layout';

const ERROR_TYPE = {
  SEARCH_BY_CODE_ERROR: 'searchByCodeError',
  SEARCH_BY_EMAIL_ERROR: 'searchByEmailError'
};

const ManageVoucher = ({ history }) => {
  const [voucherCode, setVoucherCode] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [errorType, setErrorType] = useState('');

  const [findVoucherByReference, { loading: findByRefLoading, data: findByRefData, error: findByRefError }] =
    useLazyQuery(FIND_VOUCHER_BY_REFERENCE);
  const [searchVouchers, { loading: searchLoading, data: searchData, error: searchError }] = useLazyQuery(SEARCH_VOUCHERS);

  const hasError = findByRefError || searchError || searchData?.searchVouchers?.length === 0;
  const loading = findByRefLoading || searchLoading;

  if (findByRefData && findByRefData.findVoucherByReferenceAndMarket) {
    const { alias } = findByRefData.findVoucherByReferenceAndMarket;
    history.push(`/vouchers/${alias}`);

    return null;
  }
  if (searchData && searchData.searchVouchers) {
    if (searchData.searchVouchers.length === 1) {
      history.push(`/vouchers/${searchData.searchVouchers[0].alias}`);

      return null;
    }
    if (searchData.searchVouchers.length > 1) {
      history.push(`/vouchers/search?recipientEmail=${recipientEmail}`);

      return null;
    }
  }

  return (
    <StyledManageVoucherContainer>
      <h1>
        <FormattedMessage id="vouchers.manageVoucher" />
      </h1>
      {loading && (
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      )}
      {!loading && (
        <>
          {hasError && (
            <div data-cy="voucher-not-found">
              {errorType === ERROR_TYPE.SEARCH_BY_CODE_ERROR && (
                <p>
                  <FormattedMessage id="vouchers.notFoundByCode" data-cy="voucher-not-found-by-code" />: <strong>{voucherCode}</strong>
                </p>
              )}
              {errorType === ERROR_TYPE.SEARCH_BY_EMAIL_ERROR && (
                <p>
                  <FormattedMessage id="vouchers.notFoundByEmail" data-cy="voucher-not-found-by-email" />: <strong>{recipientEmail}</strong>
                </p>
              )}
              <p>
                <strong>
                  <FormattedMessage id="vouchers.searchSuggestionsTitle" />
                </strong>
                :
              </p>
              <StyledSearchSuggestionsList>
                <li>
                  <FormattedMessage id="vouchers.searchSuggestionSpelling" />
                </li>
                <li>
                  <FormattedMessage id="vouchers.searchSuggestionsAlternative" />
                </li>
              </StyledSearchSuggestionsList>
            </div>
          )}
          <StyledSearchByHeader>
            <FormattedMessage id={hasError ? 'vouchers.searchAgain' : 'vouchers.searchBy'} />:
          </StyledSearchByHeader>
          <SearchVoucherByCodeForm
            onSubmitSearch={(values) => {
              setVoucherCode(values.voucherCode);
              setErrorType(ERROR_TYPE.SEARCH_BY_CODE_ERROR);

              return findVoucherByReference({
                variables: {
                  reference: values.voucherCode,
                  voucherType: VOUCHER_TYPE_PREPAID
                }
              });
            }}
          />
          <SearchVoucherByEmailForm
            onSubmitSearch={(values) => {
              setRecipientEmail(values.recipientEmail);
              setErrorType(ERROR_TYPE.SEARCH_BY_EMAIL_ERROR);

              return searchVouchers({
                variables: {
                  email: values.recipientEmail
                }
              });
            }}
          />
        </>
      )}
    </StyledManageVoucherContainer>
  );
};

ManageVoucher.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired
};
export default withRouter(ManageVoucher);
